import React, { useEffect, useId, useState } from 'react';
import { Alert, Box, Button, Modal, Typography, useMediaQuery } from '@mui/material';
import { keyframes } from '@mui/material/styles';

/**
 * Overlay padrão de carregamento inicial de tela (REEDY-114, spec 107 D7).
 *
 * API (a tela só informa o estado e os destinos; os tempos e a aparência são do componente):
 *   <LoadingOverlay loading={loading} error={loadFailed} onRetry={reload} onBack={goBack} />
 *   - `loading`: o carregamento INICIAL está em andamento. Refetch após ação (salvar, criar,
 *     remover) NÃO deve usar o overlay: mantenha `loading` falso nesses casos.
 *   - `error`: o carregamento falhou. Mostra a falha na hora (sem esperar os 300 ms).
 *   - `onRetry`: refaz o carregamento (a tela volta a `loading` verdadeiro e `error` falso;
 *     o tempo de espera recomeça). Ignore respostas atrasadas de tentativas anteriores.
 *   - `onBack`: destino do "Voltar" (lista: histórico com fallback para `/`; detalhe: `/evo-webhooks`).
 *   Enquanto `loading` ou `error` forem verdadeiros a tela deve renderizar só o overlay.
 *
 * Tempos: aparece após 300 ms de carregamento; aos 20 s mostra um aviso; aos 60 s vira falha
 * ("tempo esgotado": a requisição não é cancelada, só a espera da tela termina).
 */

const SHOW_AFTER_MS = 300;
const SLOW_AFTER_MS = 20_000;
const TIMEOUT_AFTER_MS = 60_000;

// 0 = oculto, 1 = carregando, 2 = demorando, 3 = tempo esgotado
type Stage = 0 | 1 | 2 | 3;

const reveal = keyframes`
  0% { clip-path: inset(100% 0 0 0); opacity: 1; }
  72% { clip-path: inset(0 0 0 0); opacity: 1; }
  88% { clip-path: inset(0 0 0 0); opacity: 0; }
  100% { clip-path: inset(100% 0 0 0); opacity: 0; }
`;

// Símbolo oficial da Reedy (design system). A cópia desbotada é só um estado de carregamento.
const ReedySymbol: React.FC<{ gradientId: string }> = ({ gradientId }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 103.77 105.37"
    aria-hidden="true"
    focusable="false"
    style={{ display: 'block', width: '100%', height: 'auto' }}
  >
    <defs>
      <linearGradient id={gradientId} x1="0" y1="52.69" x2="103.77" y2="52.69" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#3a7bf5" />
        <stop offset="1" stopColor="#4197fc" />
      </linearGradient>
    </defs>
    <path
      fill={`url(#${gradientId})`}
      d="M3.85.16c1.2-.27,2.48-.18,3.66.11,9.64,4.17,20.58,6.16,30.21,10.15,5.89,2.44,9.91,5.64,13.36,11.05C60.23,6.38,81.33,7.07,96.05.17c4.51-.64,6.74,1.77,7.12,6.09,1.91,21.58-1.49,45.93,0,67.81-.03,3.09-3.21,6.3-5.9,7.55-11.97,5.55-31.17,6.62-39.46,17.34-2.67,3.46-2.51,7.53-8.61,6.12-2.25-.52-4.78-5.89-6.58-7.87-6.37-7-23.95-10.68-33.12-14.21-4.65-1.79-8.48-2.73-9.5-8.44L.25,4.03C.89,2.47,2.07.57,3.85.16ZM10.48,12.51v59.05c0,.31,2.74,1.88,3.34,2.14,8.11,3.38,20.09,5.07,27.21,10.16,1.22.87,2.04,2.13,3.32,2.9-.52-14.73.71-29.95.03-44.62-.42-8.97-.65-16.49-9.33-21.07L10.48,12.51ZM84.98,25.19l-20.55,6.15c-2.81,1.49-1.46,5.38,1.55,5.08,4.02-1.99,17.68-3.94,20.25-6.7,1.43-1.54.81-4.39-1.25-4.53ZM84.07,43.05l-19.14,5.73c-3.09,1.73-2.82,5.35.97,5.49,3-1.28,19.44-5.01,20.57-6.87s-.26-4.37-2.4-4.35ZM82.59,60.97c-2.26.42-15.89,4.77-17.66,5.76-3.16,1.76-2.58,5.31.98,5.49l19.68-6.26c2.89-2.85,1.09-5.74-2.99-4.99Z"
    />
  </svg>
);

export interface LoadingOverlayProps {
  /** Carregamento inicial em andamento. */
  loading: boolean;
  /** O carregamento falhou: mostra a falha imediatamente. */
  error?: boolean;
  /** "Tentar novamente": a tela refaz o carregamento. */
  onRetry: () => void;
  /** "Voltar": destino definido pela tela. */
  onBack: () => void;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ loading, error = false, onRetry, onBack }) => {
  const gradientBase = useId().replace(/:/g, '');
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [stage, setStage] = useState<Stage>(0);
  // Muda a cada "Tentar novamente" para recomeçar a contagem mesmo se `loading` não oscilar.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!loading) {
      setStage(0);
      return undefined;
    }
    // Depois de "Tentar novamente" o overlay já estava na tela: volta direto ao carregamento.
    if (attempt > 0) setStage(1);
    const timers = [
      attempt > 0 ? undefined : setTimeout(() => setStage(1), SHOW_AFTER_MS),
      setTimeout(() => setStage(2), SLOW_AFTER_MS),
      setTimeout(() => setStage(3), TIMEOUT_AFTER_MS),
    ];
    return () => timers.forEach((timer) => timer && clearTimeout(timer));
  }, [loading, attempt]);

  const failed = error || (loading && stage === 3);
  const visible = failed || (loading && stage >= 1);
  if (!visible) return null;

  const handleRetry = () => {
    setAttempt((current) => current + 1);
    onRetry();
  };

  const animated = !failed && !reducedMotion;
  return (
    <Modal open hideBackdrop sx={{ zIndex: (theme) => theme.zIndex.modal }}>
      <Box
        role="status"
        aria-label={!failed && stage === 1 ? 'Carregando' : undefined}
        aria-live="polite"
        aria-busy={!failed}
        data-testid="loading-overlay"
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          p: 3,
          bgcolor: 'rgba(255,255,255,0.8)',
          cursor: failed ? 'default' : 'progress',
          outline: 'none',
        }}
      >
        <Box sx={{ position: 'relative', width: 76.8, maxWidth: '60vw' }}>
          {reducedMotion && !failed ? (
            <ReedySymbol gradientId={`${gradientBase}-full`} />
          ) : (
            <>
              <Box sx={{ filter: 'grayscale(1)', opacity: failed ? 0.4 : 0.18 }}>
                <ReedySymbol gradientId={`${gradientBase}-faded`} />
              </Box>
              {animated && (
                <Box
                  data-testid="loading-overlay-fill"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    animation: `${reveal} 2.2s ease-in-out infinite`,
                  }}
                >
                  <ReedySymbol gradientId={`${gradientBase}-color`} />
                </Box>
              )}
            </>
          )}
        </Box>
        {failed ? (
          <>
            <Alert severity="error" sx={{ maxWidth: 360 }}>
              Não foi possível carregar esta tela.
            </Alert>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={onBack}>Voltar</Button>
              <Button variant="contained" onClick={handleRetry}>
                Tentar novamente
              </Button>
            </Box>
          </>
        ) : stage === 2 ? (
          <Typography variant="body2" color="text.secondary" align="center">
            Está demorando mais que o normal…
          </Typography>
        ) : null}
      </Box>
    </Modal>
  );
};

export default LoadingOverlay;
